'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const Relay = require('react-relay/classic');
const widgetLoader = require('dibs-widget-loader').default;

// Components
const classnames = require('classnames');
const ContextComponent = require('../../global/components/ContextComponent');
const { MediaQuery, queries } = require('dibs-media-query');
const ShippingAndReturnsColumn = require('./Shipping/ShippingAndReturnsColumn');
const TradeWelcomeMessage = require('../../global/components/TradeWelcomeMessage/TradeWelcomeMessage');
const Similar = require('./Similar/Similar');
const Breadcrumbs = require('./Breadcrumbs/Breadcrumbs');
const Carousel = require('./Carousel/Carousel');
const Sidebar = require('./Sidebar/Sidebar');
const PdpSocialIcons = require('./social/PdpSocialIcons');
const ExpandableContent = require('./ExpandableContent/ExpandableContent');
const ProfileLink = require('./ProfileLink/ProfileLink');
const { Connected: Details } = require('./Details/Details');
const ContactDealerBody = require('./ContactDealer/ContactDealerBody');
const { GridDealerRelayContainer } = require('./Grid/GridDealer');
const { GridSuggestionRelayContainer } = require('./Grid/GridSuggestion');
const GridPhoto = require('./Grid/GridPhoto');
const SeoLinks = require('./SeoLinks/SeoLinks');
const PdpFolderItem = require('./Favorites/PdpFolderItem').default;
const PdpFavoriteItem = require('./Favorites/PdpFavoriteItem').default;
const ConditionReportModal = require('../../contact/dealer/components/ConditionReportModal');
const BuyerAssuranceBox = require('./BuyerAssurance/BuyerAssuranceBox');
const RequestShippingQuoteModal = require('dibs-request-shipping-modal').default;
const RecentlyViewedItems = require('./RecentlyViewedItems/RecentlyViewedItems').default;

// Helpers
const SV = require('server-vars');
const { root } = require('../../global/windowCheck.es');
const { isMasquerading } = require('dibs-cookie-jar');
const { getShipmentPrequotes } = require('../components/Shipping/shippingHelpers.es');
const pdpTracking = require('../../newPdp/helpers/pdpTracking');
const enabler = require('../../global/utils/enabler');
const get = require('lodash.get');
const PurchaseTracking = require('../../global/mixins/purchaseTracking');
const makeOfferLauncher = require('dibs-buyer-layout/exports/makeOfferLauncher').default;
const {
    trackMakeOfferModalOpened,
} = require('../../components/ItemActionButtons/MakeOfferButton/trackingHelpers.es');
const {
    loadOnboardingModalWithOrigin,
    ORIGIN_FAVORITE,
    ORIGIN_FOLDER,
} = require('dibs-buyer-layout/src/utils/tradeOnboardingHelper');

// Actions
const modalActions = require('../actions/modals.es');
const { trackPinterestClick } = require('../actions/pdpTrackingActions');
const contactDealerActions = require('../actions/contactDealerActions.es');
const { abExperiments } = require('dibs-buyer-layout/src/entries/index');

class PdpLayout extends ContextComponent {
    constructor(props) {
        super(props);
        this._contactDealer = this._contactDealer.bind(this);
        this.handleCtaClick = this.handleCtaClick.bind(this);

        this.state = {
            contactDealerCTATestRunning: false,
            isRequestShippingQuoteModalOpen: false,
            isConditionReportModalOpen: false,
            isRemoveContactDealerCTATestRunning: false,
        };
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.priceDisplays.showNetPrice && this.props.priceDisplays.showNetPrice) {
            pdpTracking.updateGaPageSubtype(this.props.priceDisplays);
        }
    }

    componentDidMount() {
        const props = this.props;
        const { item, urlData, priceDisplays, viewer, canonicalUrl, url: pageUrl } = props;
        const modalValue = get(urlData, 'queryKey.modal');
        const referredFromAmp = get(urlData, 'queryKey.userComesFromAmp');
        const ctaSelected = get(urlData, 'queryKey.ctaSelected');
        const actions = {
            // needs to keep the space b/c of the way the queryKey parses query params with hyphens
            'contact dealer': modalActions.contactDealer,
            'request net price': modalActions.netPrice,
        };
        const action = actions[modalValue];
        const ampTrackingLabel = ctaSelected === 'requestPrice' ? ' - request price button' : '';

        const tracking = {
            'contact dealer': 'contact link in pdp – deeplink',
            ampContact: `contact link in pdp${ampTrackingLabel}–amppage`,
        };
        const trackingValue = tracking[referredFromAmp ? 'ampContact' : modalValue];
        const modalOptions = {
            id: props.itemId,
            itemPk: props.itemPk,
            dealerPk: props.dealerPk,
            vertical: props.itemVertical,
            tracking: trackingValue,
        };
        // Setup the Facebook API for sharing items.
        widgetLoader.load('facebook');

        // This call kicks off the logic that results in the initial tracking pageview for the pdp.
        // No tracking will happen until after this finishes.
        pdpTracking.updateGaReturns(item.get('returnPolicy.accept') === 'Y');

        if (priceDisplays) {
            pdpTracking.updateGaPageSubtype(priceDisplays);
        }

        pdpTracking.setupShippingStatusTracking();
        pdpTracking.unblockCustomVar();

        // Track initial page load.
        // TODO: Migrate this to new pdp tracking file.
        let trackingOverrides = {};
        if (item.isSold() || item.isUnavailableStatus()) {
            trackingOverrides = pdpTracking.checkListingStatus(
                item.getListings().get('metadata.groupedListingStatuses')
            );
        }
        pdpTracking.trackProductDetail(trackingOverrides);

        const pageContext = 'pdp';
        const url = canonicalUrl || pageUrl;
        // Open the make offer modal on page load if there is query param modal=offer,
        // item is negotiable, and item has not been sold
        if (modalValue === 'offer' && item.isNegotiablePrice() && !item.isSold()) {
            makeOfferLauncher(
                Object.assign(
                    {
                        itemId: props.itemPk,
                        linkLocation: pageContext,
                        currency: props.currency,
                    },
                    props
                )
            );

            trackMakeOfferModalOpened({
                purchaseTracking: new PurchaseTracking({ item, pageContext }),
                url,
                isTrade: viewer && viewer.user && viewer.user.isVerifiedTrade,
            });
        }

        if (action) {
            if (modalValue === 'request net price') {
                const netPriceTracking = {
                    pageContext,
                    url,
                    emailTmplName: urlData.queryKey.e_tmpl || '',
                };
                const netPriceModalOptions = Object.assign({}, modalOptions, {
                    item: props.item,
                    currency: props.currency,
                    context: this.context,
                    tracking: netPriceTracking.pageContext,
                });

                this.context.executeAction(action, netPriceModalOptions);
            } else {
                this.context.executeAction(action, modalOptions);
            }
        }
        // AB test for GROWTH-1908: IMbedded offer on contact dealer
        if (abExperiments.contactDealerSubmitOffer) {
            abExperiments.contactDealerSubmitOffer.then(res => {
                this.context.executeAction(contactDealerActions.setOptimizelyVariant, {
                    optimizely: res,
                });
            });
        }

        if (abExperiments.contactDealerCTA) {
            abExperiments.contactDealerCTA.then(() => {
                this.setState({ contactDealerCTATestRunning: true });
            });
        }

        const cookie = root.document.cookie || '';
        if (abExperiments.removeContactDealerCTA && !isMasquerading(cookie)) {
            abExperiments.removeContactDealerCTA.then(() => {
                this.setState({ isRemoveContactDealerCTATestRunning: true });
            });
        }
    }

    /**
     * Passed along to Sidebar
     * @param props
     * @private
     */
    _contactDealer({ isRequestMoreImages, linkLocation }) {
        const { contactDealerCTATestRunning } = this.state;
        const { viewer, itemPk, dealerPk } = this.props;
        const { isCustomizable } = viewer.item;

        const tracking =
            linkLocation ||
            (isCustomizable
                ? 'contact link in pdp - sidebar - customize this item link'
                : 'contact link in pdp - sidebar');

        const isVerifiedTrade = get(viewer, 'user.isVerifiedTrade');
        const showPreContactDealer = contactDealerCTATestRunning && isVerifiedTrade;

        const payload = {
            tracking,
            itemPk,
            dealerPk,
            currentStep: showPreContactDealer ? 'preContactDealer' : 'contactDealer',
            handleCtaClick: this.handleCtaClick,
            isVerifiedTrade,
            isRequestMoreImages,
        };
        payload.modalContentData = isRequestMoreImages ? { copySuffix: 'requestMoreImages' } : {};
        this.context.executeAction(modalActions.contactDealer, payload);
    }

    _getTradeMessage() {
        const { tradeSalesRep } = this.props.tradeMessage;
        if (enabler.isEnabled('trade_welcome_message') && tradeSalesRep) {
            return (
                <TradeWelcomeMessage
                    {...this.props.tradeMessage}
                    tradeTour={this.props.tradeTour}
                />
            );
        }
        return null;
    }

    handleCtaClick({ action }) {
        switch (action) {
            case 'openShippingQuoteModal':
                this.setState({ isRequestShippingQuoteModalOpen: true });
                break;

            case 'openConditionReportModal':
                this.setState({ isConditionReportModalOpen: true });
                break;
        }
    }

    render() {
        const {
            hasUserId,
            loadPortfolioData,
            userId,
            userIds,
            selectedItemIds,
            shouldFillInFolder,
        } = this.props;
        const {
            contactDealerCTATestRunning,
            isRequestShippingQuoteModalOpen,
            isConditionReportModalOpen,
        } = this.state;
        const props = this.props;
        const {
            canonicalUrl,
            contactDealerErrors,
            context,
            copy,
            currency,
            creatorContent,
            dealerData,
            dealerPk,
            dealerRelatedItems,
            favoriteDealer,
            favoriteDealerCta,
            goodRelationsMeta,
            hasTrackingFired,
            heroCarouselIndex,
            heroExpanded,
            isAnyTourOpen,
            isDealerMessageSending,
            isDealerMessageSent,
            isNegotiablePrice,
            isPrintNP,
            isPrintPage,
            isReturnable,
            item: backboneItem,
            itemCategories,
            itemDescription,
            itemIsSold,
            itemIsUnlisted,
            customizationNote,
            itemId,
            itemImages,
            itemPk,
            itemTitle,
            itemVertical,
            offer,
            offerAmount,
            pageData,
            price,
            recommendedCarousel,
            internalLinks,
            retailPrice,
            socialData,
            title,
            topRecommendations,
            trackingVpvParams,
            user: backboneUser,
            viewer = null,
            userDisplayName,
            userMessage,
            recommendedPhotos,
            relay,
            sellerResponseTime,
        } = props;
        const { availableDeliveryOptions, user = null, item = null } = viewer || {};
        const isTrade = get(viewer, 'user.isVerifiedTrade') || false;
        const quotes = getShipmentPrequotes(viewer || {});
        const creatorNoPrint = creatorContent && !isPrintPage;
        const itemData = {
            title: title,
            image: (itemImages[0] || {}).small,
            seller: dealerData.company || '',
            price: retailPrice,
        };
        const pdpFavoriteItemRelayVariables = {
            loadPortfolioData,
            userId,
            userIds,
            selectedItemIds,
        };

        const isRemoveContactDealerCTATestRunning = this.state.isRemoveContactDealerCTATestRunning;

        // Sidebar needs to have higher z-index then `none` (new-pdp/pdp-layout.scss) that tour tooltips
        // would be above the content so adding the class `is-high` when any tour is open
        const pdpSidebarContainerClass = classnames('pdp-sidebar-container', {
            'is-high': isAnyTourOpen,
        });

        const sidebarProps = {
            context,
            currency,
            dealerData,
            dealerPk,
            favoriteDealer,
            favoriteDealerCta,
            itemId,
            itemPk,
            vertical: itemVertical,
            relay,
            relayItem: item,
            viewer: viewer || null,
            user: user,
            userId,
            hasUserId,
            quotes,
            availableDeliveryOptions: availableDeliveryOptions || null,
            showReducedFrom: pageData.isSaturdaySale,
            contactDealerClick: this._contactDealer,
            onFavorited: loadOnboardingModalWithOrigin.bind(null, ORIGIN_FAVORITE),
            onAddItemSuccess: loadOnboardingModalWithOrigin.bind(null, ORIGIN_FOLDER),
            selectedItemIds,
            shouldFillInFolder,
            isRemoveContactDealerCTATestRunning,
            ...pdpFavoriteItemRelayVariables,
        };

        dealerData.type = 'seller';

        const contactDealerBodyProps = {
            backboneItem,
            backboneUser,
            canonicalUrl,
            contactDealerCTATestRunning,
            isRemoveContactDealerCTATestRunning,
            copy,
            currency,
            dealerData,
            errors: contactDealerErrors,
            favoriteDealer,
            favoriteDealerCta,
            handleContactDealerCtaClick: this.handleCtaClick,
            hasTrackingFired,
            hasUserId,
            isDealerMessageSending,
            isDealerMessageSent,
            isNegotiable: isNegotiablePrice,
            isPreQuote: !!quotes.shipmentQuotes.length,
            isReturnable,
            item: item,
            itemId,
            itemPk,
            offer,
            offerAmount,
            price,
            title,
            trackingVpvParams,
            user: user,
            userDisplayName,
            userId,
            userIds,
            userMessage,
            vertical: itemVertical,
            viewer,
            sellerResponseTime,
        };

        const relatedItemsProps = {
            userId,
            viewer,
            selectedItemIds,
            copy,
            shouldFillInFolder,
            loadPortfolioData,
            onFavorited: loadOnboardingModalWithOrigin.bind(null, ORIGIN_FAVORITE),
            onAddItemSuccess: loadOnboardingModalWithOrigin.bind(null, ORIGIN_FOLDER),
            ...pdpFavoriteItemRelayVariables,
        };

        const modalHeart = (
            <PdpFavoriteItem
                showCircle
                userId={userId}
                itemId={itemId}
                item={item}
                viewer={viewer}
                onFavorited={loadOnboardingModalWithOrigin.bind(null, ORIGIN_FAVORITE)}
                {...pdpFavoriteItemRelayVariables}
            />
        );

        const itemImg = (itemImages[0] || {}).small;
        const selectedItems = [
            {
                itemId,
                src: itemImg.src,
                title,
            },
        ];

        return (
            <div className="pdp-layout boxSizingWrapper">
                <div className="js-notification-container-middle" />
                {topRecommendations && (
                    <Similar
                        items={topRecommendations}
                        itemIsUnavailable={itemIsSold || itemIsUnlisted}
                        copy={copy}
                        title={itemTitle}
                        currency={currency}
                    />
                )}
                <MediaQuery query={queries.xl}>
                    <div className="pdp-breadcrumbs-container">
                        <Breadcrumbs categories={itemCategories} />
                    </div>
                </MediaQuery>
                <meta content={goodRelationsMeta} />
                <div className="rowFlex pdp-main">
                    <div
                        className="pdp-carousel-container colXs12 colLg7 colXl8"
                        ref={carousel => {
                            this._carousel = carousel;
                        }}
                    >
                        <div className="pdp-carousel-favorite-item-container">
                            <div className="pdp-carousel-favorite-item">
                                <PdpFavoriteItem
                                    userId={userId}
                                    itemId={itemId}
                                    viewer={viewer}
                                    item={item}
                                    onFavorited={loadOnboardingModalWithOrigin.bind(
                                        null,
                                        ORIGIN_FAVORITE
                                    )}
                                    {...pdpFavoriteItemRelayVariables}
                                />
                                <PdpFolderItem
                                    itemId={itemId}
                                    viewer={viewer}
                                    item={item}
                                    shouldFillInFolder={shouldFillInFolder}
                                    onAddItemSuccess={loadOnboardingModalWithOrigin.bind(
                                        null,
                                        ORIGIN_FOLDER
                                    )}
                                    {...pdpFavoriteItemRelayVariables}
                                />
                            </div>
                        </div>
                        <Carousel
                            context={context}
                            title={itemTitle}
                            images={itemImages}
                            index={heroCarouselIndex}
                            itemId={itemId}
                            item={backboneItem}
                            socialData={socialData}
                            selectedItems={selectedItems}
                            relay={relay}
                            heartComponent={modalHeart}
                            isPrintPage={isPrintPage}
                            onContactDealerClick={this._contactDealer}
                            isRemoveContactDealerCTATestRunning={
                                isRemoveContactDealerCTATestRunning
                            }
                            itemData={itemData}
                        />
                        <PdpSocialIcons
                            context={context}
                            socialData={socialData}
                            selectedItems={selectedItems}
                            relay={relay}
                            pinterestConnectAccounts
                            onPinterestPin={() => this.context.executeAction(trackPinterestClick)}
                            index={heroCarouselIndex}
                            viewer={viewer}
                            user={user}
                            itemData={itemData}
                        />
                    </div>
                    <div className={`${pdpSidebarContainerClass} colXs12 colLg5 colXl4`}>
                        <Sidebar {...sidebarProps} />
                    </div>
                </div>
                <div className="rowFlex">
                    <div className="pdp-hero-container colXs12 colLg9">
                        {isPrintPage ? null : (
                            <ExpandableContent
                                title={copy.heroTitles.about}
                                description={itemDescription}
                                expanded={heroExpanded}
                                isPrintHero={false}
                            />
                        )}

                        {customizationNote ? (
                            <ExpandableContent
                                title={copy.heroTitles.customizationOptions}
                                description={customizationNote}
                                tracking={false}
                            />
                        ) : null}
                    </div>
                </div>
                <div className="rowFlex">
                    <div className="pdp-hero-container flex-columm colXs12 colLg9">
                        <Details context={context} />
                    </div>
                    {!isPrintNP ? (
                        <div className="pdp-hero-container flex-column colLg9">
                            <ShippingAndReturnsColumn
                                hasUserId={hasUserId}
                                availableDeliveryOptions={availableDeliveryOptions || null}
                                context={context}
                                item={item}
                                quotes={quotes}
                                viewer={viewer}
                                relay={relay}
                                itemIsSold={itemIsSold}
                                itemIsUnlisted={itemIsUnlisted}
                                itemId={itemId}
                                userId={userId}
                                dealerPk={dealerPk}
                                user={user}
                                favoriteDealer={favoriteDealer}
                                favoriteDealerCta={favoriteDealerCta}
                                dealerData={dealerData}
                            />
                        </div>
                    ) : null}

                    {dealerData.maker && dealerData.maker.description ? (
                        <div className="pdp-creator-container colXs12 colLg9">
                            <h2 className="pdp-hero-about header-primary-large pdp-secondary-header">
                                {copy.heroTitles.aboutTheMaker}
                            </h2>

                            <ProfileLink
                                caption={dealerData.company}
                                url={dealerData.uri}
                                label={copy.linkText.viewProfileLinktext}
                                image={dealerData.maker.headshot}
                            />

                            <ExpandableContent
                                description={dealerData.maker.description}
                                tracking={false}
                            />
                        </div>
                    ) : null}

                    {creatorNoPrint ? (
                        <div className="pdp-creator-container colXs12 colLg9">
                            <ExpandableContent
                                description={creatorContent.description}
                                title={copy.heroTitles.about}
                                name={creatorContent.name}
                                image={creatorContent.image}
                                collapseHeight={73}
                                tnType="creator"
                                tracking={false}
                            />
                        </div>
                    ) : null}
                </div>
                <div className="pdp-dealer-container">
                    <ContactDealerBody {...contactDealerBodyProps} />
                </div>
                <div className="pdp-assurance-container">
                    <BuyerAssuranceBox isTrade={isTrade} />
                </div>
                <div className="pdp-grid-container" data-tn="pdp-dealer-grid">
                    {dealerRelatedItems && dealerRelatedItems.items.length ? (
                        <GridDealerRelayContainer
                            itemId={itemId}
                            data={dealerRelatedItems.items}
                            dealerData={dealerData}
                            addFavoriting
                            currency={currency}
                            {...relatedItemsProps}
                        />
                    ) : null}
                </div>
                <div className="pdp-grid-container">
                    {recommendedCarousel && recommendedCarousel.items.length ? (
                        <GridSuggestionRelayContainer
                            data={recommendedCarousel.items}
                            currency={currency}
                            {...relatedItemsProps}
                        />
                    ) : null}
                </div>
                <RecentlyViewedItems itemId={itemId} />
                <div className="pdp-grid-container">
                    {recommendedPhotos.length ? (
                        <GridPhoto photos={recommendedPhotos} context={this.context} />
                    ) : null}
                </div>
                {internalLinks && (
                    <div className="pdp-seo-links-container">
                        <SeoLinks links={internalLinks} />
                    </div>
                )}

                {this._getTradeMessage()}

                {contactDealerCTATestRunning && (
                    <RequestShippingQuoteModal
                        viewer={viewer}
                        isOpened={isRequestShippingQuoteModalOpen}
                        closeShippingModal={() =>
                            this.setState({ isRequestShippingQuoteModalOpen: false })
                        }
                        userId={userId}
                        user={null}
                        itemId={itemId}
                    />
                )}
                {contactDealerCTATestRunning && (
                    <ConditionReportModal
                        user={user}
                        item={item}
                        isOpened={isConditionReportModalOpen}
                        closeModal={() => this.setState({ isConditionReportModalOpen: false })}
                    />
                )}
            </div>
        );
    }
}

PdpLayout.propTypes = {
    ctas: PropTypes.object.isRequired,
    priceDisplays: PropTypes.object.isRequired,
    detailsTitles: PropTypes.object.isRequired,
    details: PropTypes.object.isRequired,
    detailsConfig: PropTypes.array.isRequired,
    shippingMessage: PropTypes.string,
    currency: PropTypes.string,
    currencyLoaded: PropTypes.bool,
    heroCarouselIndex: PropTypes.number.isRequired,
    heroExpanded: PropTypes.bool.isRequired,
    hold: PropTypes.array,
    isAnyTourOpen: PropTypes.bool,
    item: PropTypes.object.isRequired,
    itemId: PropTypes.string.isRequired,
    itemCategories: PropTypes.object.isRequired,
    itemCreationDate: PropTypes.string,
    itemDescription: PropTypes.string.isRequired,
    itemHidePrice: PropTypes.bool.isRequired,
    itemImages: PropTypes.array.isRequired,
    itemPrice: PropTypes.number,
    itemInitialPrice: PropTypes.number,
    itemIsSold: PropTypes.bool.isRequired,
    itemIsUnlisted: PropTypes.bool.isRequired,
    itemTitle: PropTypes.string.isRequired,
    itemVertical: PropTypes.string.isRequired,
    itemPk: PropTypes.string.isRequired,
    isSoldIndividually: PropTypes.bool.isRequired,
    itemCanMakePurchase: PropTypes.bool.isRequired,
    creatorContent: PropTypes.object,
    dealerData: PropTypes.object.isRequired,
    dealerRelatedItems: PropTypes.object.isRequired,
    selectedItemIds: PropTypes.array.isRequired,
    canonicalUrl: PropTypes.string.isRequired,
    dealerPk: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    askingPrice: PropTypes.number,
    itemIsOnHold: PropTypes.bool.isRequired,
    internalLinks: PropTypes.array,
    socialData: PropTypes.object.isRequired,
    returnPolicy: PropTypes.object.isRequired,
    returnMessage: PropTypes.string.isRequired,
    goodRelationsMeta: PropTypes.string,
    copy: PropTypes.object.isRequired,
    favoriteDealer: PropTypes.object.isRequired,
    isPrintPage: PropTypes.bool,
    pageData: PropTypes.shape({
        isSaturdaySale: PropTypes.bool,
    }).isRequired,
    userId: PropTypes.string,
    recommendedPhotos: PropTypes.array,
    sellerResponseTime: PropTypes.string,
};

module.exports = Relay.createContainer(PdpLayout, {
    initialVariables: {
        itemId: SV.get('data.pk'),
        userId: null,
        useIPAddress: true,
        hasUserId: false,
        first: 10,
        regionType: null,
        selectedItemIds: [],
        shouldFillInFolder: false,
        loadPortfolioData: false,
        shouldFetchItem: true,
        shouldFetchTransaction: false,
        userIds: [],
        itemIds: [],
    },
    prepareVariables: prevVariables => {
        const { itemId, userIds } = prevVariables;
        const itemIds = [itemId];
        const shouldFetchTransaction = !!(userIds && userIds.length);
        return Object.assign({}, prevVariables, { itemIds, shouldFetchTransaction });
    },
    fragments: {
        viewer: ({
            userId,
            hasUserId,
            itemId,
            selectedItemIds,
            shouldFillInFolder,
            userIds,
            loadPortfolioData,
            itemIds,
            shouldFetchTransaction,
            shouldFetchItem,
        }) => Relay.QL`
            fragment on Viewer {
                ${RequestShippingQuoteModal.getFragment('viewer', {
                    hasUserId,
                    itemId,
                    userId,
                    userIds,
                    itemIds,
                    shouldFetchTransaction,
                    shouldFetchItem,
                })}
                ${PdpFavoriteItem.getFragment('viewer', {
                    itemId,
                    loadPortfolioData,
                    selectedItemIds,
                    userId,
                    userIds,
                })}
                ${PdpFolderItem.getFragment('viewer', {
                    itemId,
                    loadPortfolioData,
                    selectedItemIds,
                    shouldFillInFolder,
                    userId,
                    userIds,
                })}
                ${ShippingAndReturnsColumn.getFragment('viewer', { hasUserId, itemId, userId })}
                ${Sidebar.getFragment('viewer', {
                    hasUserId,
                    itemId,
                    loadPortfolioData,
                    selectedItemIds,
                    shouldFillInFolder,
                    userId,
                    userIds,
                })}
                ${ContactDealerBody.getFragment('viewer', { userId, userIds, itemId, hasUserId })}
                ${GridDealerRelayContainer.getFragment('viewer', {
                    itemId,
                    loadPortfolioData,
                    selectedItemIds,
                    shouldFillInFolder,
                    userId,
                    userIds,
                })}
                ${GridSuggestionRelayContainer.getFragment('viewer', {
                    itemId,
                    loadPortfolioData,
                    selectedItemIds,
                    shouldFillInFolder,
                    userId,
                    userIds,
                })}
                ${PdpSocialIcons.getFragment('viewer')}
                user(userId: $userId) @include(if: $hasUserId) {
                    ${ShippingAndReturnsColumn.getFragment('user', { hasUserId, itemId, userId })}
                    ${Sidebar.getFragment('user', {
                        hasUserId,
                        itemId,
                        loadPortfolioData,
                        selectedItemIds,
                        shouldFillInFolder,
                        userId,
                        userIds,
                    })}
                    ${ContactDealerBody.getFragment('user', { userId, userIds, itemId, hasUserId })}
                    ${ConditionReportModal.getFragment('user', { userId })}
                    ${PdpSocialIcons.getFragment('user')}
                    isVerifiedTrade
                }
                item(itemId: $itemId) {
                    ${ShippingAndReturnsColumn.getFragment('item', { hasUserId, itemId, userId })}
                    ${Sidebar.getFragment('relayItem', {
                        hasUserId,
                        itemId,
                        loadPortfolioData,
                        selectedItemIds,
                        shouldFillInFolder,
                        userId,
                        userIds,
                    })}
                    ${ContactDealerBody.getFragment('item', { userId, userIds, itemId, hasUserId })}
                    ${ConditionReportModal.getFragment('item', { userId })}
                    ${PdpFavoriteItem.getFragment('item', {
                        itemId,
                        loadPortfolioData,
                        selectedItemIds,
                        userId,
                        userIds,
                    })}
                    ${PdpFolderItem.getFragment('item', {
                        itemId,
                        loadPortfolioData,
                        selectedItemIds,
                        shouldFillInFolder,
                        userId,
                        userIds,
                    })}
                    isCustomizable
                }
                shipmentQuotes(itemId: $itemId, searchView: PDP, first: $first) {
                    edges {
                        node {
                            handlingTime {
                                numberOfUnits
                            }
                            handlingTimeDisplay
                            buyerSubTotalAmount {
                                currency
                                amount
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            }
                            is1stdibsBilledQuote
                            is1stdibsWhiteGloveQuote
                            serviceId
                            serviceMethod {
                                serviceMethodType
                                serviceType
                                displayName
                                region {
                                    apiType
                                    code
                                    regionName
                                    regionType
                                    regionBoundary {
                                        type {
                                            name
                                        }
                                    }
                                }
                                windowMin {
                                    numberOfUnits
                                }
                            }
                        }
                    }
                }
                localDeliveryOptions: availableDeliveryOptions(itemId: $itemId, regionView: LOCAL, first: $first, addressResolutionType: IP) {
                    edges {
                        node {
                            handlingTimeDisplay
                            buyerSubTotalAmount {
                                currency
                                amount
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            }
                            is1stdibsWhiteGloveQuote
                            serviceMethod {
                                serviceMethodType
                                displayName
                                region {
                                    apiType
                                    code
                                    regionName
                                    regionType
                                }
                            }
                        }
                    }
                }
                domesticDeliveryOptions: availableDeliveryOptions(itemId: $itemId, regionView: DOMESTIC, first: $first, addressResolutionType: IP) {
                    edges {
                        node {
                            handlingTimeDisplay
                            buyerSubTotalAmount {
                                currency
                                amount
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            }
                            is1stdibsWhiteGloveQuote
                            serviceMethod {
                                serviceMethodType
                                displayName
                                region {
                                    apiType
                                    code
                                    regionName
                                    regionType
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
});



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/PdpLayout.jsx